import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";
import { withRouter, useHistory, RouteComponentProps } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";

import { BreadCrumb } from "../../../components/Common";
import {
  ActionButton,
  AppTable,
  ColumnHeader,
  DateColumn,
  TableSpinner,
  TextColumn,
} from "../../../components";
import { ActionTypeEnum } from "../../../enums";
import DeleteModal from "../../../components/Common/DeleteModal";
import { AddEditEducationSystemModal } from "./AddEditEducationSystemModal";
import { EducationSystemDto } from "../../../interfaces/curriculum";
import { PaginateQuery } from "../../../interfaces";
import {
  useGetEducationSystemsQuery,
  useDeleteEducationSystemMutation,
} from "../../../store/api/educationSystemApi";

type TProps = RouteComponentProps & {};

const EducationSystemsPageComponent: React.FC<TProps> = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const initState = new PaginateQuery(10, "sequence_number:ASC");
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  const { data: response, isLoading: loading } =
    useGetEducationSystemsQuery(paginateQuery);
  const [deleteEducationSystem, { isLoading: deleting, error: deleteError }] =
    useDeleteEducationSystemMutation();

  document.title = `${t("documentTitle", { pageName: "educationSystems" })}`;

  const columns: TableColumn<EducationSystemDto>[] = [
    {
      name: <ColumnHeader title="sequenceNumber" />,
      width: "90px",
      cell: (row) => <TextColumn text={`#${row.sequenceNumber ?? "-"}`} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="icon" />,
      width: "80px",
      cell: (row) =>
        row.icon?.path ? (
          <img
            src={row.icon.path}
            alt=""
            className="rounded"
            style={{ width: 36, height: 36, objectFit: "cover" }}
          />
        ) : (
          <span className="text-muted">—</span>
        ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="name" />,
      cell: (row) => (
        <button
          type="button"
          className="btn btn-link p-0 text-primary text-decoration-underline"
          onClick={() =>
            history.push(
              `/education-system/curricula?filter[educationSystemId]=${row.id}`
            )
          }
        >
          {row.name[i18n.language === "ar" ? "ar" : "en"]}
        </button>
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="code" />,
      cell: (row) => <TextColumn text={row.code} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="description" />,
      cell: (row) => (
        <TextColumn
          text={
            row.description?.[i18n.language === "ar" ? "ar" : "en"] || "—"
          }
        />
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="action" />,
      sortable: false,
      cell: (row) => (
        <React.Fragment>
          <Button
            color="info"
            outline
            size="sm"
            className="me-1"
            title={t("curricula")}
            onClick={() =>
              history.push(
                `/education-system/curricula?filter[educationSystemId]=${row.id}`
              )
            }
          >
            <i className="ri-book-open-line me-1"></i>
            {t("curricula")}
          </Button>
          <ActionButton
            onAction={() => {
              setEditingItemId(row.id);
              setShowAddEditModal(true);
            }}
            actionType={ActionTypeEnum.EDIT}
          />
          &nbsp;
          <ActionButton
            onAction={() => {
              setDeletingItemId(row.id);
              setShowDeleteModal(true);
            }}
            actionType={ActionTypeEnum.DELETE}
          />
        </React.Fragment>
      ),
    },
  ];

  useEffect(() => {
    if (paginateQuery !== initState) {
      const { limit, page, search, sortBy } = paginateQuery;
      let query = `?page=${page || 1}`;
      if (limit) query += `&limit=${limit}`;
      if (sortBy) query += `&sortBy=${sortBy}`;
      if (search) query += `&search=${search}`;
      history.replace({ search: query });
    }
  }, [paginateQuery]);

  const onDeleteConfirm = async () => {
    try {
      await deleteEducationSystem(deletingItemId as string).unwrap();
      setDeletingItemId(null);
      setShowDeleteModal(false);
    } catch (err) {}
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("educationSystems")}
            pageTitle={t("educationSystem")}
          />
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) =>
                            setPaginateQuery({
                              ...paginateQuery,
                              search: event.target.value,
                              page: 1,
                            })
                          }
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col className="col-sm-auto ms-auto">
                      <Button
                        color="success"
                        onClick={() => {
                          setEditingItemId(null);
                          setShowAddEditModal(true);
                        }}
                      >
                        <i className="ri-add-fill me-1 align-bottom"></i>
                        {t("addItem", { item: "educationSystem" })}
                      </Button>
                    </Col>
                  </Row>
                  <AppTable
                    theme={"dark"}
                    columns={columns}
                    data={response?.data || []}
                    progressPending={loading}
                    progressComponent={<TableSpinner />}
                    pagination
                    paginationServer
                    onChangePage={(page: number) =>
                      setPaginateQuery({ ...paginateQuery, page })
                    }
                    onChangeRowsPerPage={(limit: number, page: number) =>
                      setPaginateQuery({ ...paginateQuery, limit, page })
                    }
                    persistTableHead
                    paginationTotalRows={response?.meta?.totalItems}
                    paginationComponentOptions={{
                      rowsPerPageText: t("rowsPerPage"),
                      rangeSeparatorText: t("of"),
                    }}
                    fixedHeader={true}
                    fixedHeaderScrollHeight="60vh"
                  />
                  <DeleteModal
                    loading={deleting}
                    show={showDeleteModal}
                    onDeleteClick={onDeleteConfirm}
                    onCloseClick={() => setShowDeleteModal(false)}
                    error={
                      deleteError
                        ? (deleteError as any)?.data?.message ||
                          "An error occurred"
                        : undefined
                    }
                  />
                  <AddEditEducationSystemModal
                    id={editingItemId}
                    show={showAddEditModal}
                    onCloseClick={() => {
                      setShowAddEditModal(false);
                      setEditingItemId(null);
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export const EducationSystemsPage = withRouter(EducationSystemsPageComponent);
